import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { IBooking } from "redux/reducers/models/teetime";
import { convertHoles, convertTime } from "helpers/Helpers";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";

import Panel from "components/panel/Panel";
import Sheet from "components/sheet/Sheet";
import { Chip } from "components/Chip/Chip";
import { Select } from "components/select/index";

import type { IMoveBookingTeeTimeData, TMoveBookingDaypart } from "./portalMoveBookingTeeTimes";

import "./portalMoveBooking.scss";

const { Option } = Select;

export interface IPortalMoveBookingSummary {
  holes: number;
  players: number;
}

export interface IPortalMoveBookingProps {
  open: boolean;
  onClose: () => void;
  teeTimeData: IMoveBookingTeeTimeData;
  targetTeeTimeId: number | null;
  onTargetTeeTimeIdChange: (id: number | null) => void;
  currentBooking: IBooking | null;
  summary: IPortalMoveBookingSummary;
  confirmLoading: boolean;
  onConfirm: () => void;
}

function daypartHeading(daypart: TMoveBookingDaypart, t: (key: string) => string): string {
  switch (daypart) {
    case "morning":
      return t("Morning");
    case "afternoon":
      return t("Afternoon");
    case "evening":
      return t("Evening");
    default:
      return "";
  }
}

export default function PortalMoveBooking(props: IPortalMoveBookingProps) {
  const {
    open,
    onClose,
    teeTimeData,
    targetTeeTimeId,
    onTargetTeeTimeIdChange,
    currentBooking,
    summary,
    confirmLoading,
    onConfirm,
  } = props;

  const {
    fetchedTeeTimeCount,
    eligibleTeeTimes,
    eligibleTeeTimesOrdered,
    daypartSections,
  } = teeTimeData;

  const { t } = useTranslation();
  const windowSize = useWindowSize();

  const teeTime = currentBooking?.tee_time;
  const course = teeTime?.course;
  const currentDateLabel =
    teeTime?.date != null && teeTime.date !== ""
      ? moment(String(teeTime.date)).format("ddd MMM D, YYYY")
      : "";
  const currentTimeLabel = convertTime(teeTime?.start_time ?? "");
  const currentWhenLabel =
    currentDateLabel !== "" && currentTimeLabel !== ""
      ? `${currentDateLabel} · ${currentTimeLabel}`
      : currentDateLabel || currentTimeLabel;

  const holesPlayersLabel = `${t("Holes")} · ${t("Players")}`;
  const holesPlayersValue = `${convertHoles(summary.holes, course)} ${t("Holes")} · ${summary.players} ${
    summary.players === 1 ? t("Player") : t("Players")
  }`;

  function renderMoveBookingModalBody() {
    return (
      <div className="portal-move-booking-modal">
        {currentBooking?.tee_time ? (
          <section className="portal-move-booking-summary" aria-label={t("Current booking")}>
            <dl className="portal-move-booking-summary__dl">
              <div className="portal-move-booking-summary__row">
                <dt className="portal-move-booking-summary__label">{t("Current time")}</dt>
                <dd className="portal-move-booking-summary__value">{currentWhenLabel}</dd>
              </div>
              <div className="portal-move-booking-summary__row">
                <dt className="portal-move-booking-summary__label">{holesPlayersLabel}</dt>
                <dd className="portal-move-booking-summary__value">{holesPlayersValue}</dd>
              </div>
            </dl>
          </section>
        ) : null}

        <div className="portal-move-booking-modal__field portal-move-booking-modal__field--select">
          {fetchedTeeTimeCount === 0 ? (
            <p className="portal-move-booking-modal__empty">{t("No available tee times for this date.")}</p>
          ) : eligibleTeeTimes.length === 0 ? (
            <p className="portal-move-booking-modal__empty">
              {t("No tee times have enough space for your group on this date.")}
            </p>
          ) : (
            <>
              <Select
                usePortal
                timeSelect
                showPlaceholder
                label={t("Available tee times")}
                placeholder={t("Select a time")}
                disabled={eligibleTeeTimesOrdered.length === 0}
                onChange={(id: number) => onTargetTeeTimeIdChange(id)}
                className="portal-move-booking-tee-select mt-2 mb-4"
                defaultValue={targetTeeTimeId}
              >
                {eligibleTeeTimesOrdered.map(teetime => {
                  const label = convertTime(teetime.start_time ?? "");
                  return (
                    <Option key={teetime.id} value={teetime.id} name={label}>
                      {label}
                    </Option>
                  );
                })}
              </Select>

              <div className="portal-move-booking-dayparts">
                {daypartSections.map(section => (
                  <div key={section.daypart} className="portal-move-booking-daypart">
                    <p className="portal-move-booking-daypart__label">{daypartHeading(section.daypart, t)}</p>
                    <div className="portal-move-booking-daypart__slots" role="list">
                      {section.teeTimes.map(teetime => {
                        const selected = targetTeeTimeId === teetime.id;
                        const disabled = targetTeeTimeId != null && !selected;
                        return (
                          <div
                            key={teetime.id}
                            className="portal-move-booking-daypart__slot"
                            role="listitem"
                          >
                            <Chip
                              className="portal-move-booking-slot"
                              label={convertTime(teetime.start_time ?? "")}
                              size="md"
                              selected={selected}
                              disabled={disabled}
                              onClick={() =>
                                selected
                                  ? onTargetTeeTimeIdChange(null)
                                  : onTargetTeeTimeIdChange(teetime.id)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const confirmLabel = t("Confirm move");
  const confirmDisabled = !targetTeeTimeId || eligibleTeeTimes.length === 0;

  return (
    <>
      <div className="portal-move-booking-host">
        <Panel
          open={open && windowSize.width < MOBILE_WIDTH}
          onClose={onClose}
          title={t("Move booking")}
          animate
          loading={confirmLoading}
          primaryAction={{
            onClick: () => void onConfirm(),
            label: confirmLabel,
            disabled: confirmDisabled,
          }}
          secondaryAction={{
            onClick: onClose,
            label: t("Cancel"),
          }}
        >
          <div className="portal-move-booking-panel-inner">{renderMoveBookingModalBody()}</div>
        </Panel>
      </div>

      <div className="portal-move-booking-host">
        <Sheet
          open={open && windowSize.width > MOBILE_WIDTH}
          size="small"
          closable
          title={t("Move booking")}
          onCancel={onClose}
          onOk={(): void => {
            void onConfirm();
          }}
          cancelText={t("Cancel")}
          okText={confirmLabel}
          okDisabled={confirmDisabled}
          okLoading={confirmLoading}
        >
          {renderMoveBookingModalBody()}
        </Sheet>
      </div>
    </>
  );
}
